import { fontFor } from '../format'
import type { InspectorSection, InspectorTabs, Tone } from '../spec/types'

/**
 * The right-hand inspector: what the selected row IS, where it came from, and
 * what has been done to it.
 *
 * Verbatim from `docs/design/reference/template.html` lines 700–751.
 *
 * Provenance is the first tab by design. In a system where our ledger is the
 * operational record and FinXP's is the legally authoritative one (invariant
 * 2), a figure without its source is a figure somebody will quote to the wrong
 * party. The raw payload is behind a reveal for the same reason it exists at
 * all: it is the provider's own words, verbatim, for when the rendering is
 * disputed.
 */

const TAB_LABELS = ['Provenance', 'Activity', 'Related', 'Raw'] as const

interface Props {
  readonly open: boolean
  readonly subject: string
  readonly tab: number
  readonly onTab: (tab: number) => void
  readonly sections: InspectorTabs
  readonly rawOpen: boolean
  readonly onToggleRaw: () => void
  readonly onToggle: () => void
}

export function Inspector({
  open,
  subject,
  tab,
  onTab,
  sections,
  rawOpen,
  onToggleRaw,
  onToggle,
}: Props) {
  const active = sections[Math.min(tab, 3)] ?? []

  return (
    <aside
      style={{
        flex: '0 0 auto',
        width: open ? '316px' : '38px',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '2px solid var(--k-rule)',
        background: 'var(--k-panel)',
        overflow: 'hidden',
      }}
      aria-label="Inspector"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: '30px',
          padding: '0 10px',
          borderBottom: '1px solid var(--k-line)',
          flex: '0 0 auto',
        }}
      >
        <span
          style={{
            fontSize: '9.5px',
            letterSpacing: '.09em',
            textTransform: 'uppercase',
            color: 'var(--k-mut2)',
          }}
        >
          {open ? 'Inspector' : ''}
        </span>
        <span
          style={{
            fontFamily: 'var(--k-mono)',
            fontSize: '11px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {open ? subject : ''}
        </span>
        <button
          type="button"
          className="k-hov"
          onClick={onToggle}
          aria-label={open ? 'Collapse inspector' : 'Expand inspector'}
          style={{
            marginLeft: 'auto',
            width: '20px',
            height: '20px',
            border: '1px solid var(--k-line)',
            background: 'transparent',
            color: 'var(--k-ink)',
            fontFamily: 'var(--k-mono)',
            fontSize: '11px',
            cursor: 'pointer',
            flex: '0 0 auto',
          }}
        >
          {open ? '›' : '‹'}
        </button>
      </div>

      {open && (
        <>
          <div style={{ display: 'flex', gap: '1px', background: 'var(--k-line)', flex: '0 0 auto' }}>
            {TAB_LABELS.map((label, i) => (
              <button
                key={label}
                type="button"
                className="k-hov"
                onClick={() => onTab(i)}
                data-cur={tab === i ? '1' : ''}
                style={{
                  flex: 1,
                  height: '24px',
                  border: 0,
                  background: 'var(--k-panel)',
                  color: 'var(--k-ink)',
                  font: 'inherit',
                  fontSize: '10px',
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
            {active.map((section) => (
              <Section
                key={section.title}
                section={section}
                rawOpen={rawOpen}
                onToggleRaw={onToggleRaw}
              />
            ))}
          </div>
        </>
      )}
    </aside>
  )
}

function Section({
  section,
  rawOpen,
  onToggleRaw,
}: {
  section: InspectorSection
  rawOpen: boolean
  onToggleRaw: () => void
}) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <div
        style={{
          fontSize: '9.5px',
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--k-mut2)',
          marginBottom: '5px',
        }}
      >
        {section.title}
      </div>

      {section.k === 'kv' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px 10px',
            fontSize: '11.5px',
          }}
        >
          {(section.items ?? []).map((item) => (
            <div key={item[0]} style={{ display: 'contents' }}>
              <span
                style={{
                  color: 'var(--k-mut2)',
                  fontFamily: 'var(--k-mono)',
                  fontSize: '10.5px',
                }}
              >
                {item[0]}
              </span>
              <span
                style={{
                  fontFamily: fontFor(item[2]),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item[1]}
              </span>
            </div>
          ))}
        </div>
      )}

      {section.k === 'feed' && (
        <div>
          {(section.items ?? []).map((item) => (
            <div
              key={`${item[0]}-${item[3] ?? ''}`}
              style={{ padding: '6px 0', borderBottom: '1px solid var(--k-line)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span
                  data-tone={(item[2] ?? 'n') as Tone}
                  style={{
                    padding: '0 4px',
                    fontSize: '9px',
                    fontWeight: 800,
                    letterSpacing: '.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {item[0]}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--k-mono)',
                    fontSize: '10.5px',
                    color: 'var(--k-mut2)',
                    marginLeft: 'auto',
                  }}
                >
                  {item[3]}
                </span>
              </div>
              <div style={{ fontSize: '11.5px', marginTop: '3px', textWrap: 'pretty' }}>
                {item[1]}
              </div>
              <div style={{ fontSize: '10.5px', color: 'var(--k-mut)', marginTop: '2px' }}>
                {item[4]}
              </div>
            </div>
          ))}
        </div>
      )}

      {section.k === 'raw' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
            <button
              type="button"
              className="k-hov"
              onClick={onToggleRaw}
              style={{
                height: '22px',
                padding: '0 8px',
                border: '1px solid var(--k-line)',
                background: 'transparent',
                color: 'var(--k-ink)',
                font: 'inherit',
                fontSize: '10.5px',
                cursor: 'pointer',
              }}
            >
              {rawOpen ? 'Hide raw payload' : 'Reveal raw payload'}
            </button>
            <span style={{ fontSize: '10.5px', color: 'var(--k-mut2)' }}>{section.note}</span>
          </div>
          {rawOpen && (
            <pre
              style={{
                margin: 0,
                padding: '8px',
                background: 'var(--k-bg)',
                border: '1px solid var(--k-line)',
                fontFamily: 'var(--k-mono)',
                fontSize: '10.5px',
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                color: 'var(--k-mut)',
                maxHeight: '260px',
                overflow: 'auto',
              }}
            >
              {section.raw}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}
